if (!customElements.get('frequently-bought-together')) {
  customElements.define(
    'frequently-bought-together',
    class FrequentlyBoughtTogether extends HTMLElement {
      constructor() {
        super();

        this.items = Array.from(this.querySelectorAll('[data-fbt-item]'));
        this.baseItem = this.querySelector('[data-is-base="true"]');
        this.currentPriceElement = this.querySelector('[data-fbt-summary-current-price]');
        this.comparePriceElement = this.querySelector('[data-fbt-compare-price]');
        this.savingsElement = this.querySelector('[data-fbt-savings]');
        this.messageElement = this.querySelector('[data-fbt-message]');
        this.addButton = this.querySelector('[data-fbt-add]');
        this.addButtonLabel = this.querySelector('[data-fbt-button-label]');
        this.addButtonSpinner = this.addButton?.querySelector('.loading__spinner');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.discountType = (this.dataset.discountType || 'none').toLowerCase();
        this.discountValue = Number(this.dataset.discountValue || 0);
        this.currencyCode = this.dataset.currencyCode || 'USD';
        this.buttonLabel = this.dataset.buttonLabel || 'Add';
        this.variantSelects = Array.from(this.querySelectorAll('[data-fbt-variant-select]'));
        this.variantDropdowns = Array.from(this.querySelectorAll('[data-fbt-variant-dropdown]'));

        this.onToggleChange = this.onToggleChange.bind(this);
        this.onVariantSelectChange = this.onVariantSelectChange.bind(this);
        this.onVariantTriggerClick = this.onVariantTriggerClick.bind(this);
        this.onVariantOptionClick = this.onVariantOptionClick.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
        this.handleAddToCart = this.handleAddToCart.bind(this);
      }

      connectedCallback() {
        this.items.forEach((item) => {
          const toggle = item.querySelector('[data-bundle-toggle]');
          if (toggle) toggle.addEventListener('change', this.onToggleChange);
        });

        this.variantSelects.forEach((select) => {
          select.addEventListener('change', this.onVariantSelectChange);
        });

        this.variantDropdowns.forEach((dropdown) => {
          dropdown.querySelector('[data-fbt-variant-trigger]')?.addEventListener('click', this.onVariantTriggerClick);
          dropdown.querySelectorAll('[data-fbt-variant-option]').forEach((option) => {
            option.addEventListener('click', this.onVariantOptionClick);
          });
          this.syncVariantDropdown(dropdown);
        });

        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onDocumentKeydown);

        this.addButton?.addEventListener('click', this.handleAddToCart);
        this.subscribeToVariantChanges();
        this.updateSummary();
      }

      disconnectedCallback() {
        this.items.forEach((item) => {
          const toggle = item.querySelector('[data-bundle-toggle]');
          if (toggle) toggle.removeEventListener('change', this.onToggleChange);
        });

        this.variantSelects.forEach((select) => {
          select.removeEventListener('change', this.onVariantSelectChange);
        });

        this.variantDropdowns.forEach((dropdown) => {
          dropdown.querySelector('[data-fbt-variant-trigger]')?.removeEventListener('click', this.onVariantTriggerClick);
          dropdown.querySelectorAll('[data-fbt-variant-option]').forEach((option) => {
            option.removeEventListener('click', this.onVariantOptionClick);
          });
        });

        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onDocumentKeydown);

        this.addButton?.removeEventListener('click', this.handleAddToCart);

        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
          this.variantChangeUnsubscriber = undefined;
        }
      }

      onToggleChange() {
        this.hideMessage();
        this.updateSummary();
      }

      onVariantSelectChange(event) {
        const select = event.target;
        const selectedOption = select.options[select.selectedIndex];
        const item = select.closest('[data-fbt-item]');

        if (!item || !selectedOption) return;

        item.dataset.variantId = selectedOption.value || '';
        item.dataset.price = selectedOption.dataset.price || '0';
        item.dataset.available = selectedOption.dataset.available || 'true';
        item.classList.toggle('is-disabled', item.dataset.available !== 'true');

        const priceElement = item.querySelector('[data-fbt-item-price]');
        if (priceElement) {
          priceElement.textContent = this.formatMoney(Number(item.dataset.price || 0));
        }

        const imageElement = item.querySelector('.fbt-item__image');
        if (imageElement && selectedOption.dataset.image) {
          imageElement.src = selectedOption.dataset.image;
        }

        this.syncVariantDropdown(select.closest('[data-fbt-variant-dropdown]'));

        this.hideMessage();
        this.updateSummary();
      }

      onVariantTriggerClick(event) {
        event.preventDefault();
        const dropdown = event.currentTarget.closest('[data-fbt-variant-dropdown]');
        const menu = dropdown?.querySelector('[data-fbt-variant-menu]');
        const trigger = dropdown?.querySelector('[data-fbt-variant-trigger]');
        if (!dropdown || !menu || !trigger) return;

        const willOpen = menu.classList.contains('hidden');
        this.closeVariantMenus();

        if (willOpen) {
          dropdown.classList.add('is-open');
          menu.classList.remove('hidden');
          trigger.setAttribute('aria-expanded', 'true');
        }
      }

      onVariantOptionClick(event) {
        event.preventDefault();
        const optionButton = event.currentTarget;
        const dropdown = optionButton.closest('[data-fbt-variant-dropdown]');
        const select = dropdown?.querySelector('[data-fbt-variant-select]');
        const variantId = optionButton.dataset.variantId || '';
        if (!dropdown || !select || !variantId) return;

        select.value = variantId;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        this.closeVariantMenus();
      }

      onDocumentClick(event) {
        if (!this.contains(event.target)) return;
        if (!event.target.closest('[data-fbt-variant-dropdown]')) {
          this.closeVariantMenus();
        }
      }

      onDocumentKeydown(event) {
        if (event.key === 'Escape') {
          this.closeVariantMenus();
        }
      }

      closeVariantMenus() {
        this.variantDropdowns.forEach((dropdown) => {
          dropdown.classList.remove('is-open');
          dropdown.querySelector('[data-fbt-variant-menu]')?.classList.add('hidden');
          dropdown.querySelector('[data-fbt-variant-trigger]')?.setAttribute('aria-expanded', 'false');
        });
      }

      syncVariantDropdown(dropdown) {
        if (!dropdown) return;

        const select = dropdown.querySelector('[data-fbt-variant-select]');
        const selectedOption = select?.options?.[select.selectedIndex];
        if (!selectedOption) return;

        const currentTitle = dropdown.querySelector('[data-fbt-current-variant-title]');
        const currentPrice = dropdown.querySelector('[data-fbt-current-variant-price]');
        const currentImage = dropdown.querySelector('.fbt-variant-dropdown__thumb');
        const selectedTitle = selectedOption.dataset.title || selectedOption.textContent || '';
        const selectedPrice = this.formatMoneyWithoutCurrency(Number(selectedOption.dataset.price || 0));
        const selectedImage = selectedOption.dataset.image || '';

        if (currentTitle) currentTitle.textContent = selectedTitle;
        if (currentPrice) currentPrice.textContent = selectedPrice;

        if (currentImage) {
          if (selectedImage) {
            currentImage.src = selectedImage;
            currentImage.alt = selectedTitle;
            currentImage.classList.remove('hidden');
          } else {
            currentImage.removeAttribute('src');
            currentImage.classList.add('hidden');
          }
        }

        dropdown.querySelectorAll('[data-fbt-variant-option]').forEach((option) => {
          const isSelected = option.dataset.variantId === selectedOption.value;
          option.classList.toggle('is-selected', isSelected);
          option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        });
      }

      subscribeToVariantChanges() {
        if (window.PUB_SUB_EVENTS && typeof subscribe === 'function') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const variant = event?.data?.variant;
            if (!variant || !this.baseItem) return;
            this.updateBaseVariant(variant);
          });
        }
      }

      updateBaseVariant(variant) {
        this.baseItem.dataset.variantId = String(variant.id || '');
        this.baseItem.dataset.price = String(variant.price || 0);
        this.baseItem.dataset.available = variant.available ? 'true' : 'false';
        this.baseItem.classList.toggle('is-disabled', !variant.available);

        const baseSelect = this.baseItem.querySelector('[data-fbt-variant-select]');
        if (baseSelect) {
          baseSelect.value = String(variant.id || '');
          this.syncVariantDropdown(baseSelect.closest('[data-fbt-variant-dropdown]'));
        }

        const priceElement = this.baseItem.querySelector('[data-fbt-item-price]');
        if (priceElement) {
          priceElement.textContent = this.formatMoney(Number(variant.price || 0));
        }

        const imageElement = this.baseItem.querySelector('.fbt-item__image');
        const featuredImage = variant.featured_image || variant.featured_media;
        let featuredImageUrl = '';
        if (imageElement && featuredImage?.src) {
          imageElement.src = featuredImage.src;
          if (featuredImage.alt) imageElement.alt = featuredImage.alt;
          featuredImageUrl = featuredImage.src;
        }

        if (baseSelect && featuredImageUrl) {
          const selectedOption = baseSelect.options[baseSelect.selectedIndex];
          if (selectedOption) {
            selectedOption.dataset.image = featuredImageUrl;
            this.syncVariantDropdown(baseSelect.closest('[data-fbt-variant-dropdown]'));
          }
        }

        this.updateSummary();
      }

      getSelectedItems() {
        return this.items.filter((item) => {
          if (item.dataset.available !== 'true') return false;
          if (item.dataset.isBase === 'true') return true;

          const toggle = item.querySelector('[data-bundle-toggle]');
          return Boolean(toggle && toggle.checked);
        });
      }

      updateSummary() {
        const selectedItems = this.getSelectedItems();
        const selectedCount = selectedItems.length;
        const subtotal = selectedItems.reduce((total, item) => total + Number(item.dataset.price || 0), 0);
        const qualifiesForDiscount =
          selectedCount > 1 && selectedItems.some((item) => item.dataset.isBase === 'true');

        const { discountedTotal, savings } = this.getDiscountBreakdown(selectedItems, qualifiesForDiscount);
        const hasDiscount = qualifiesForDiscount && savings > 0;

        if (this.currentPriceElement) {
          this.currentPriceElement.textContent = this.formatMoney(discountedTotal);
        }

        if (this.comparePriceElement) {
          this.comparePriceElement.textContent = this.formatMoney(subtotal);
          this.comparePriceElement.classList.toggle('hidden', !hasDiscount);
        }

        if (this.savingsElement) {
          this.savingsElement.textContent = hasDiscount ? `You save ${this.formatMoney(savings)}` : '';
          this.savingsElement.classList.toggle('hidden', !hasDiscount);
        }

        if (!this.addButton) return;

        const baseAvailable = this.baseItem?.dataset.available !== 'false';
        this.addButton.disabled = !selectedCount || !baseAvailable;
        if (this.addButtonLabel) {
          this.addButtonLabel.textContent = `${this.buttonLabel} ${selectedCount} to Cart`;
        }

        if (!baseAvailable) {
          this.showMessage('The selected base variant is currently unavailable.', true);
        } else if (!selectedCount) {
          this.showMessage('Select at least one product to continue.', true);
        } else {
          this.hideMessage();
        }
      }

      getDiscountBreakdown(selectedItems, qualifiesForDiscount) {
        const subtotal = selectedItems.reduce((total, item) => total + Number(item.dataset.price || 0), 0);

        if (!qualifiesForDiscount || !subtotal) {
          return {
            discountedTotal: subtotal,
            savings: 0,
          };
        }

        const savings = selectedItems.reduce((totalDiscount, item) => {
          const itemPrice = Number(item.dataset.price || 0);

          if (!itemPrice) return totalDiscount;

          switch (this.discountType) {
            case 'amount':
            case 'fixed_amount': {
              const fixedDiscount = Math.min(itemPrice, Math.round(this.discountValue * 100));
              return totalDiscount + fixedDiscount;
            }
            case 'percentage': {
              const percentageDiscount = Math.floor((itemPrice * this.discountValue) / 100);
              return totalDiscount + percentageDiscount;
            }
            default:
              return totalDiscount;
          }
        }, 0);

        return {
          discountedTotal: Math.max(0, subtotal - savings),
          savings,
        };
      }

      formatMoney(amountInCents) {
        const amount = Number(amountInCents || 0) / 100;
        const currencyCode = this.currencyCode || 'NZD';

        try {
          const numberPortion = new Intl.NumberFormat(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(amount);

          const symbolPart = new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: currencyCode,
            currencyDisplay: 'narrowSymbol',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
            .formatToParts(amount)
            .find((part) => part.type === 'currency')?.value;

          const currencySymbol = symbolPart || '$';
          return `${currencySymbol}${numberPortion} ${currencyCode}`;
        } catch (error) {
          return `$${amount.toFixed(2)} ${currencyCode}`;
        }
      }

      formatMoneyWithoutCurrency(amountInCents) {
        const withCurrency = this.formatMoney(amountInCents);
        const currencyCode = this.currencyCode || 'NZD';
        return withCurrency.endsWith(` ${currencyCode}`)
          ? withCurrency.slice(0, -(` ${currencyCode}`).length)
          : withCurrency;
      }

      buildBundleId() {
        return `fbt-${this.dataset.sectionId || 'section'}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      }

      getBundleProperties(bundleId) {
        return {
          _fbt_bundle_id: bundleId,
          _fbt_bundle_name: this.dataset.bundleName || 'Frequently Bought Together',
          _fbt_discount_type: this.discountType,
          _fbt_discount_value: String(this.discountValue || 0),
          _fbt_base_product_id: String(this.dataset.baseProductId || ''),
        };
      }

      async getUpdatedCartSections() {
        if (!this.cart || typeof this.cart.getSectionsToRender !== 'function') return null;

        const sectionIds = this.cart
          .getSectionsToRender()
          .map((section) => section.id)
          .join(',');

        if (!sectionIds) return null;

        const response = await fetch(`${window.routes?.cart_url || '/cart'}?sections=${encodeURIComponent(sectionIds)}`);
        if (!response.ok) return null;

        const sections = await response.json();
        return {
          id: this.baseItem?.dataset.variantId || '',
          sections,
        };
      }

      async handleAddToCart(event) {
        event.preventDefault();

        const selectedItems = this.getSelectedItems();
        if (!selectedItems.length) {
          this.showMessage('Select at least one product to continue.', true);
          return;
        }

        const baseItem = this.baseItem || selectedItems[0];
        const baseVariantId = Number(baseItem?.dataset.variantId || 0);
        const bundleId = this.buildBundleId();

        const items = selectedItems
          .map((item) => {
            const isBase = item.dataset.isBase === 'true';
            const variantId = Number(item.dataset.variantId || 0);

            return {
              id: variantId,
              quantity: 1,
              ...(isBase || !baseVariantId ? {} : { parent_id: baseVariantId }),
              properties: {
                ...this.getBundleProperties(bundleId),
                _fbt_role: isBase ? 'parent' : 'child',
                _fbt_product_id: String(item.dataset.productId || ''),
                _fbt_product_handle: item.dataset.productHandle || '',
              },
            };
          })
          .filter((item) => item.id);

        if (!items.length) return;

        this.setLoading(true);

        try {
          const body = { items };

          if (this.cart) {
            body.sections = this.cart.getSectionsToRender().map((section) => section.id).join(',');
            body.sections_url = window.location.pathname;
            this.cart.setActiveElement(document.activeElement);
          }

          const config =
            typeof fetchConfig === 'function'
              ? fetchConfig('json')
              : {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                  },
                };

          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          config.body = JSON.stringify(body);

          const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, config);
          const responseData = await response.json();

          if (!response.ok || responseData.status) {
            throw new Error(responseData.description || responseData.message || 'Unable to add bundle to cart.');
          }

          if (this.cart) {
            if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');

            const updatedCart = (await this.getUpdatedCartSections()) || responseData;

            if (updatedCart?.sections) {
              this.cart.renderContents(updatedCart);
            } else {
              window.location = window.routes?.cart_url || '/cart';
              return;
            }
          } else {
            window.location = window.routes?.cart_url || '/cart';
            return;
          }

          this.showMessage('Bundle added to cart.');
        } catch (error) {
          console.error('Frequently bought together error:', error);
          this.showMessage(error.message || 'Unable to add bundle to cart.', true);
        } finally {
          this.setLoading(false);
        }
      }

      setLoading(isLoading) {
        if (!this.addButton) return;

        this.addButton.classList.toggle('loading', isLoading);
        this.addButton.disabled = isLoading || !this.getSelectedItems().length;
        this.addButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
        if (this.addButtonSpinner) {
          this.addButtonSpinner.classList.toggle('hidden', !isLoading);
        }

        if (!isLoading) {
          this.updateSummary();
        }
      }

      showMessage(message, isError = false) {
        if (!this.messageElement) return;

        this.messageElement.textContent = message;
        this.messageElement.classList.remove('hidden');
        this.messageElement.classList.toggle('fbt__message--error', isError);
      }

      hideMessage() {
        if (!this.messageElement) return;

        this.messageElement.textContent = '';
        this.messageElement.classList.add('hidden');
        this.messageElement.classList.remove('fbt__message--error');
      }
    }
  );
}
